import React, { useState } from 'react'
import moment from 'moment'
import FormStep from '../../../../ui/form/FormStep'
import DynamicForm from '../../../../ui/form/DynamicForm'
import ContinueButton from '../../containers/CarrierWizard/components/ContinueButton'
import { useStepNavigator } from '../../../../ui/StepNavigator'
import {
    sendFedexRestGeneratePin,
    sendFedexRestValidateAddress,
    sendFedexRestValidateInvoice,
    sendFedexRestValidatePin,
    updateState,
    useCarrierManagement,
} from '../../carrierManagement'
import { markValidated } from 'reducers/modules/integrations'
import { useDispatch } from 'react-redux'

type MfaMethod = 'INVOICE' | 'PIN' | null

const TECH_SUPPORT_ERROR_MESSAGE =
    'We are unable to process this request. Please try again later or call FedEx Customer Service and ask for technical support.'

const invoiceForm = {
    fields: [
        {
            name: 'number',
            title: 'Invoice Number',
            validate: ['nonempty'],
        },
        {
            name: 'date',
            title: 'Invoice Date',
            type: 'date',
            validate: 'nonempty',
            hint: 'Must be within the last 90 days',
        },
        {
            name: 'currency',
            title: 'Currency Code',
            validate: 'nonempty',
            hint: 'e.g. USD, CAD, EUR',
        },
        {
            name: 'amount',
            title: 'Invoice Amount',
            validate: 'nonempty',
            hint: 'Amount with up to 2 decimal places',
        },
    ],
}

const PIN_OPTIONS = [
    { value: 'SMS', label: 'Text Message (SMS)' },
    { value: 'CALL', label: 'Phone Call' },
    { value: 'EMAIL', label: 'Email' },
]

function MethodSelection({ onSelect }) {
    return (
        <div data-testid="mfa-method-selection">
            <h3>Select Verification Method</h3>
            <p>
                Choose how you would like to verify your FedEx account.
            </p>
            <div className="mfa-method-buttons" style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' }}>
                <button
                    type="button"
                    className="btn btn-default btn-block text-left"
                    onClick={() => onSelect('INVOICE')}
                    data-testid="mfa-select-invoice"
                >
                    <strong>Invoice Verification</strong>
                    <div className="text-muted" style={{ fontSize: '13px' }}>
                        Verify using details from a recent FedEx invoice (within 90 days)
                    </div>
                </button>
                <button
                    type="button"
                    className="btn btn-default btn-block text-left"
                    onClick={() => onSelect('PIN')}
                    data-testid="mfa-select-pin"
                >
                    <strong>PIN Verification</strong>
                    <div className="text-muted" style={{ fontSize: '13px' }}>
                        Receive a 6-digit PIN via SMS, phone call, or email
                    </div>
                </button>
            </div>
        </div>
    )
}

function InvoiceVerification({ onSuccess, onBack, onTechSupportRequired }) {
    const context = useCarrierManagement()
    const {
        carrier,
        state: { carrierEntity, fedexRestCarrierId } = {},
    } = context
    const [errors, setErrors] = useState<string[] | null>(null)
    const [techSupportRequired, setTechSupportRequired] = useState(false)
    const dispatchRedux = useDispatch()
    const { nextStep } = useStepNavigator()

    const handleSubmit = async (values) => {
        setErrors(null)

        const invoice = {
            ...values,
            date: moment(values.date).format('YYYY-MM-DD'),
            amount: parseFloat(values.amount).toFixed(2),
        }

        const result = await sendFedexRestValidateInvoice(context, {
            id: fedexRestCarrierId,
            invoice,
        })

        if (result?.ok) {
            updateState(context, {
                carrierDisabled: !result.can_activate,
                validated: true,
                validationSuccess: true,
                validationErrors: null,
                contactSupport: null,
                installed: true,
            })

            dispatchRedux(markValidated(carrier, carrierEntity))
            nextStep()
        } else {
            const errorMessages = result?.errors || ['Invoice verification failed']
            const isTechSupportError = errorMessages.some((msg: string) =>
                /locked|max.*(attempt|retr)|unable to process/i.test(msg)
            )

            if (isTechSupportError) {
                setTechSupportRequired(true)
            } else {
                setErrors(errorMessages)
            }
        }
    }

    if (techSupportRequired) {
        return <TechSupportFallback onBack={onBack} />
    }

    return (
        <FormStep name="invoice_verification" initialValues={{ currency: 'USD' }}>
            {({ values, hasValidationErrors }) => (
                <div data-testid="invoice-verification">
                    <h3>Invoice Verification</h3>
                    <p>
                        Enter details from a recent FedEx invoice (within the
                        last 90 days) to verify your account.
                    </p>

                    {errors && (
                        <div className="alert alert-danger">
                            {errors.map((err, i) => (
                                <div key={i}>{err}</div>
                            ))}
                        </div>
                    )}

                    <div className="account-info-form compact-form">
                        <DynamicForm
                            tab={invoiceForm}
                            values={values}
                            forceRowStyle="col-md-6 xsmini-col"
                            noAutocomplete
                        />
                    </div>

                    <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                        <button
                            type="button"
                            className="btn btn-link"
                            onClick={onBack}
                        >
                            &larr; Back
                        </button>
                        <ContinueButton
                            errors={hasValidationErrors}
                            onClick={async () => {
                                await handleSubmit(values)
                            }}
                            label="Verify Invoice"
                        />
                    </div>
                </div>
            )}
        </FormStep>
    )
}

function PinVerification({ onBack, email, phone }) {
    const context = useCarrierManagement()
    const {
        carrier,
        state: { carrierEntity, fedexRestCarrierId } = {},
    } = context
    const dispatchRedux = useDispatch()
    const { nextStep } = useStepNavigator()
    const [pinSent, setPinSent] = useState(false)
    const [selectedDelivery, setSelectedDelivery] = useState<string | null>(null)
    const [pinValue, setPinValue] = useState('')
    const [errors, setErrors] = useState<string[] | null>(null)
    const [sending, setSending] = useState(false)
    const [techSupportRequired, setTechSupportRequired] = useState(false)

    const handleSendPin = async () => {
        if (!selectedDelivery) return
        setErrors(null)
        setSending(true)

        const result = await sendFedexRestGeneratePin(context, {
            id: fedexRestCarrierId,
            option: selectedDelivery,
        })

        setSending(false)

        if (result?.ok) {
            setPinSent(true)
        } else {
            const errorMessages = result?.errors || ['Failed to send PIN. Please try another method.']
            const isTechSupportError = errorMessages.some((msg: string) =>
                /locked|max.*(attempt|retr)|unable to process/i.test(msg)
            )

            if (isTechSupportError) {
                setTechSupportRequired(true)
            } else {
                setErrors(errorMessages)
            }
        }
    }

    const handleValidatePin = async () => {
        setErrors(null)

        const result = await sendFedexRestValidatePin(context, {
            id: fedexRestCarrierId,
            pin: pinValue,
        })

        if (result?.ok) {
            updateState(context, {
                carrierDisabled: !result.can_activate,
                validated: true,
                validationSuccess: true,
                validationErrors: null,
                contactSupport: null,
                installed: true,
            })

            dispatchRedux(markValidated(carrier, carrierEntity))
            nextStep()
        } else {
            const errorMessages = result?.errors || ['Invalid PIN. Please try again.']
            const isTechSupportError = errorMessages.some((msg: string) =>
                /locked|max.*(attempt|retr)|unable to process/i.test(msg)
            )

            if (isTechSupportError) {
                setTechSupportRequired(true)
            } else {
                setErrors(errorMessages)
            }
        }
    }

    if (techSupportRequired) {
        return <TechSupportFallback onBack={onBack} />
    }

    if (!pinSent) {
        return (
            <div data-testid="pin-delivery-selection">
                <h3>PIN Verification</h3>
                <p>Select how you would like to receive your 6-digit PIN.</p>
                {(phone || email) && (
                    <p className="text-muted" style={{ fontSize: '13px', marginTop: '-8px' }}>
                        {phone && <>Registered phone: <strong>{phone}</strong>{email ? ' · ' : ''}</>}
                        {email && <>Registered email: <strong>{email}</strong></>}
                    </p>
                )}

                {errors && (
                    <div className="alert alert-danger">
                        {errors.map((err, i) => (
                            <div key={i}>{err}</div>
                        ))}
                    </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' }}>
                    {PIN_OPTIONS.map(({ value, label }) => (
                        <button
                            key={value}
                            type="button"
                            className={`btn btn-block text-left${selectedDelivery === value ? ' btn-primary' : ' btn-default'}`}
                            style={{ padding: '12px 16px' }}
                            onClick={() => setSelectedDelivery(value)}
                            data-testid={`pin-option-${value.toLowerCase()}`}
                        >
                            <strong>{label}</strong>
                        </button>
                    ))}
                </div>

                <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                    <button
                        type="button"
                        className="btn btn-link"
                        onClick={onBack}
                    >
                        &larr; Back
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSendPin}
                        disabled={!selectedDelivery || sending}
                    >
                        {sending ? 'Sending...' : 'Send PIN'}
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div data-testid="pin-entry" style={{ textAlign: 'center' }}>
            <h3>Enter Your PIN</h3>
            <p>
                A 6-digit PIN has been sent via{' '}
                <strong>{PIN_OPTIONS.find((o) => o.value === selectedDelivery)?.label ?? selectedDelivery}</strong>.
                It will be valid for 10 minutes.
            </p>

            {errors && (
                <div className="alert alert-danger" style={{ textAlign: 'left' }}>
                    {errors.map((err, i) => (
                        <div key={i}>{err}</div>
                    ))}
                </div>
            )}

            <div style={{ display: 'inline-block', margin: '8px auto 20px', textAlign: 'left' }}>
                <label htmlFor="pin-input" style={{ display: 'block', fontWeight: 600, marginBottom: '6px' }}>
                    PIN Code
                </label>
                <input
                    id="pin-input"
                    type="text"
                    className="form-control"
                    maxLength={6}
                    value={pinValue}
                    onChange={(e) => setPinValue(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    data-testid="pin-input"
                    style={{ width: '160px', fontSize: '22px', letterSpacing: '6px', textAlign: 'center' }}
                    autoFocus
                />
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                <button
                    type="button"
                    className="btn btn-link"
                    onClick={() => {
                        setPinSent(false)
                        setErrors(null)
                    }}
                >
                    &larr; Resend PIN
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleValidatePin}
                    disabled={pinValue.length !== 6}
                    data-testid="submit-pin"
                >
                    Verify PIN
                </button>
            </div>
        </div>
    )
}

function TechSupportFallback({ onBack }) {
    const context = useCarrierManagement()
    const { state: { fedexRestCarrierId, fedexRestAccountInfo } = {} } = context
    const { nextStep } = useStepNavigator()
    const dispatchRedux = useDispatch()
    const { carrier, state: { carrierEntity } = {} } = context
    const [errors, setErrors] = useState<string[] | null>(null)
    const [retrying, setRetrying] = useState(false)

    const handleRetry = async () => {
        if (!fedexRestAccountInfo) return
        setErrors(null)
        setRetrying(true)

        const response = await sendFedexRestValidateAddress(context, {
            id: fedexRestCarrierId,
            registration: fedexRestAccountInfo,
        })

        setRetrying(false)

        if (response?.ok && !response.mfa_required) {
            updateState(context, {
                carrierDisabled: !response.can_activate,
                validated: true,
                validationSuccess: true,
                validationErrors: null,
                contactSupport: null,
                installed: true,
            })

            dispatchRedux(markValidated(carrier, carrierEntity))
            nextStep()
        } else if (response?.ok && response.mfa_required) {
            setErrors(['Your account has not been authorized yet. Please ensure you have completed the verification with FedEx support, then try again.'])
        } else {
            setErrors(response?.errors || ['Verification failed. Please try again.'])
        }
    }

    return (
        <div data-testid="tech-support-fallback">
            <div className="alert alert-warning">
                {TECH_SUPPORT_ERROR_MESSAGE}
            </div>

            <p>To verify your account through FedEx tech support:</p>
            <ol style={{ paddingLeft: '20px', marginBottom: '16px' }}>
                <li style={{ marginBottom: '8px' }}>
                    Call FedEx Customer Service and ask for technical support
                </li>
                <li style={{ marginBottom: '8px' }}>
                    Answer the security questions to verify your identity
                </li>
                <li>
                    Once FedEx confirms your identity, click the button below
                    to complete registration. Note: authorization is valid for
                    30 minutes.
                </li>
            </ol>

            {errors && (
                <div className="alert alert-danger">
                    {errors.map((err, i) => (
                        <div key={i}>{err}</div>
                    ))}
                </div>
            )}

            <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                <button
                    type="button"
                    className="btn btn-link"
                    onClick={onBack}
                >
                    &larr; Back
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleRetry}
                    disabled={retrying}
                    data-testid="tech-support-retry"
                >
                    {retrying ? 'Verifying...' : "I've Contacted Support — Complete Registration"}
                </button>
            </div>
        </div>
    )
}

export const MfaVerificationStep = () => {
    const context = useCarrierManagement()
    const {
        state: { fedexRestMfaEmail, fedexRestMfaPhone } = {},
    } = context
    const [selectedMethod, setSelectedMethod] = useState<MfaMethod>(null)

    const handleBack = () => setSelectedMethod(null)

    return (
        <div data-testid="mfa-verification-step">
            {!selectedMethod && (
                <MethodSelection onSelect={setSelectedMethod} />
            )}

            {selectedMethod === 'INVOICE' && (
                <InvoiceVerification
                    onSuccess={() => {}}
                    onBack={handleBack}
                    onTechSupportRequired={() => {}}
                />
            )}

            {selectedMethod === 'PIN' && (
                <PinVerification
                    onBack={handleBack}
                    email={fedexRestMfaEmail}
                    phone={fedexRestMfaPhone}
                />
            )}
        </div>
    )
}
